import { createContext, useCallback, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type ReactNode } from 'react';

interface LocationValue { pathname: string; search: string; state: unknown }
interface NavigateOptions { replace?: boolean; state?: unknown }
type NavigateFunction = (to: string, options?: NavigateOptions) => void;

const RouterContext = createContext<{ location: LocationValue; navigate: NavigateFunction } | null>(null);

function readLocation(): LocationValue {
  return { pathname: window.location.pathname, search: window.location.search, state: window.history.state };
}

export function BrowserRouter({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(readLocation);
  useEffect(() => { const update = () => setLocation(readLocation()); window.addEventListener('popstate', update); return () => window.removeEventListener('popstate', update); }, []);
  const navigate = useCallback<NavigateFunction>((to, options = {}) => {
    window.history[options.replace ? 'replaceState' : 'pushState'](options.state ?? null, '', to);
    window.dispatchEvent(new PopStateEvent('popstate', { state: options.state }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  return <RouterContext.Provider value={{ location, navigate }}>{children}</RouterContext.Provider>;
}

function useRouter() { const context = useContext(RouterContext); if (!context) throw new Error('Router context is missing'); return context; }
export function useNavigate() { return useRouter().navigate; }
export function useLocation() { return useRouter().location; }
export function useSearchParams(): [URLSearchParams] { const { search } = useLocation(); return useMemo(() => [new URLSearchParams(search)], [search]); }
export function useParams(): Record<string, string> { const { pathname } = useLocation(); const match = pathname.match(/^\/app\/conversations\/([^/]+)$/); return match ? { conversationId: decodeURIComponent(match[1]!) } : {}; }

export function Link({ to, state, onClick, ...props }: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string; state?: unknown }) {
  const navigate = useNavigate();
  return <a href={to} {...props} onClick={(event) => { onClick?.(event); if (!event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); navigate(to, { state }); } }} />;
}

export function NavLink({ to, className = '', ...props }: Omit<Parameters<typeof Link>[0], 'className'> & { className?: string }) {
  const { pathname } = useLocation();
  const active = pathname === to || (to !== '/' && pathname.startsWith(`${to}/`));
  return <Link to={to} {...props} className={`${className} ${active ? 'active' : ''}`.trim()} aria-current={active ? 'page' : undefined} />;
}

export function Navigate({ to, replace = false, state }: { to: string; replace?: boolean; state?: unknown }) {
  const navigate = useNavigate(); useEffect(() => navigate(to, { replace, state }), [navigate, replace, state, to]); return null;
}
